import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { MessageType_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import { Direction } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { Person } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { CallResult, EntityType } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { PhoneRecord } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/models.pb';
import { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import { motion } from 'motion/react';
import { UsersQueries } from '@frontend/api-users';
import { getUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { emitOpenCallInsightPanel, emitOpenVoicemailInsightPanel } from '@frontend/inbox-call-item-event-bus';
import { getFullName } from '@frontend/name';
import { Photos } from '@frontend/photos';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { TagSelectionComponents, TagSelectionHooks } from '@frontend/tag-selection';
import { useTimestampFormatter } from '@frontend/timestamp-formatter';
import { InboxPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import { Avatar, AvatarProps, Chat, KeyNames, useAlert } from '@frontend/design-system';
import { useGetSortedTagsById, useCallRecordTagMutation } from '../../hooks';
import { ThreadItemTag } from './thread-item-tag';
import { resolveAndSortActivities } from './voice-ai-activities/utils';
import { VoiceAiActivityAccordion } from './voice-ai-activities/voice-ai-activity-accordion';

type CallThreadItemProps = {
  callItem: PhoneRecord;
  contact?: Person;
  avatarProps?: Omit<AvatarProps, 'locationId' | 'personId' | 'isUser' | 'isWeave'> & { preferredName?: string };
  hiddenContextActions?: string[];
  threadId?: string;
};

type BubbleInfo = {
  title: string;
  subtitle?: string;
  resultKey?: 'missed' | 'inbound' | 'outbound' | 'abandoned' | 'voicemail' | 'robocall';
};

export const CallThreadItem: FC<CallThreadItemProps> = ({
  callItem,
  contact,
  avatarProps,
  hiddenContextActions = [],
  threadId,
}) => {
  const { t } = useTranslation('thread-body');
  const alert = useAlert();
  const { selectedOrgId } = useAppScopeStore();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const voiceAiInUnifiedInboxEnabled = getFeatureFlagValue('nwx:voice-ai-in-unified-inbox');
  const settingsNavigate = useSettingsNavigate();
  const isInbound = callItem.direction === Direction.DIRECTION_INBOUND;
  const isRobocall = callItem.entityType === EntityType.ENTITY_TYPE_ROBOCALL;
  const user = getUser();
  const formatTimestamp = useTimestampFormatter();

  const locationUsersQueries = UsersQueries.useGetMultiLocationsUsers({
    locationIds: [callItem.locationId],
    params: { showChildren: true },
  });

  const locationUsers = useMemo(() => locationUsersQueries.flatMap(({ data }) => data || []), [locationUsersQueries]);

  const senderName = `${avatarProps?.firstName ?? ''} ${avatarProps?.lastName ?? ''}`.trim() || avatarProps?.name;

  const showTags = !hiddenContextActions?.includes('tag');

  // Tag-related state and refs
  const [tagSearchValue, setTagSearchValue] = useState('');
  const tagSelectionInputRef = useRef<HTMLInputElement>(null);
  const tagInputWidthCalcRef = useRef<HTMLSpanElement>(null);
  const callBubbleRef = useRef<HTMLLIElement>(null);
  const [tagInputWidth] = useState(0);

  const sortedCallTagIds = useGetSortedTagsById({
    orgId: selectedOrgId,
    locationIds: [callItem.locationId],
    tagIds: callItem.tags?.map((callItemTag) => callItemTag.tagId) ?? [],
    enabled: showTags,
  });

  const getV1TagFromTagId = (tagId: string) => sortedCallTagIds?.find((t) => t.id === tagId);

  // Call record tag mutation hook with unified thread cache integration
  const { applyTag, dismissTag } = useCallRecordTagMutation({
    channelId: callItem.channelId,
    threadCallId: callItem.threadCallId,
    callResult: callItem.callResult,
    entityCreatedAt: callItem.startedAt || '',
    startTime: callItem.startedAt ?? '',
    threadId,
    locationId: callItem.locationId,
    entityType: callItem.entityType,
    onApplySuccess: () => {
      alert.success(t('Tag added successfully.'));
    },
    onApplyError: () => {
      alert.error(t('Error tagging call record.'));
    },
    onDismissOptimisticSuccess: (onClick, autoDismissAfter) => {
      alert.info({
        message: t('Tag Removed'),
        action: {
          label: t('Undo'),
          onClick,
        },
        autoDismissAfter,
      });
    },
    onDismissError: () => {
      alert.error(t('Error removing tag. Please try again.'));
    },
  });

  // Tag selection popover
  const {
    popoverProps: tagPopoverProps,
    open: openTagSelection,
    isOpen: tagSelectionIsOpen,
    close: closeTagSelection,
    getTriggerProps: getTagSelectionTriggerProps,
  } = TagSelectionHooks.useTagSelectionPopover({
    groupIds: [callItem.locationId],
    onTagSelect: async (tag) => {
      applyTag(tag);
      closeTagSelection();
    },
    onClose: () => {
      setTagSearchValue('');
      closeTagSelection();
    },
    onTagCreate: (initName) => {
      settingsNavigate.navigate({
        to: '/tags/quick-create',
        context: {
          onCreate: (tag: Tag) => applyTag(tag),
          name: initName,
        },
      });
    },
    trackingIds: {
      selectTag: `${InboxPrefixes.Tag}-add-existing-call-tag`,
      createTag: `${InboxPrefixes.Tag}-create-new-call-tag`,
      createNamedTag: `${InboxPrefixes.Tag}-create-new-call-tag-with-name`,
      openTagSettings: `${InboxPrefixes.Tag}-open-tag-settings`,
    },
    searchValue: tagSearchValue,
    tagIdsToExclude: callItem.tags?.map((tag) => tag.tagId) || [],
  });

  const sendingUser = useMemo(() => {
    if (callItem.createdBy === user?.userID) {
      return {
        firstName: user?.firstName,
        lastName: user?.lastName,
      };
    }
    const foundUser = locationUsers.find((user) => user.UserID === callItem.createdBy);
    if (!foundUser) return undefined;
    return {
      firstName: foundUser.FirstName,
      lastName: foundUser.LastName,
    };
  }, [callItem, user, locationUsers]);

  const deleterName = useMemo(() => {
    if (!callItem.deletedBy) return undefined;

    if (user && callItem.deletedBy === user?.userID) {
      return getFullName(user);
    }

    const matchedUser = locationUsers.find((user) => user.UserID === callItem.deletedBy);
    if (!matchedUser) return t('Unknown User');

    return getFullName(matchedUser);
  }, [callItem.deletedBy, user, locationUsers, t]);

  const bubbleInfo: BubbleInfo = useMemo(() => {
    // Robocalls are always outbound auto-messages (appointment reminders)
    if (isRobocall) {
      // Determine subtitle based on intentType
      let subtitle = t('Appointment Reminder'); // default

      switch (callItem.intentType) {
        case MessageType_Enum.APPOINTMENT_REMINDER:
          subtitle = t('Appointment Reminder');
          break;
        case MessageType_Enum.APPOINTMENT_RECALL:
          subtitle = t('Appointment Recall');
          break;
        case MessageType_Enum.APPOINTMENT_REACTIVATION:
          subtitle = t('Appointment Reactivation');
          break;
        case MessageType_Enum.UNSPECIFIED:
        default:
          subtitle = t('Appointment Reminder');
          break;
      }

      return {
        title: t('Outbound Call'),
        subtitle,
        resultKey: 'robocall',
      };
    }

    const callResult = callItem.callResult;

    switch (callResult) {
      case CallResult.CALL_RESULT_ANSWERED:
        return isInbound
          ? {
              title: t('Inbound Call'),
              subtitle: getFullName(contact ?? {}) || contact?.firstName || contact?.preferredName || 'Anonymous user',
              resultKey: 'inbound',
            }
          : { title: t('Outbound Call'), resultKey: 'outbound' };
      case CallResult.CALL_RESULT_ABANDONED:
        return { title: t('Abandoned Call'), resultKey: 'abandoned' };
      case CallResult.CALL_RESULT_MISSED:
        return { title: t('Missed Call'), resultKey: 'missed' };
      case CallResult.CALL_RESULT_VOICEMAIL:
        return { title: t('Voicemail'), resultKey: 'voicemail' };
      default:
        return { title: t('Unknown Call') };
    }
  }, [callItem.callResult, callItem.intentType, isRobocall, isInbound, contact, t]);

  const voiceAiActivities = callItem.voiceAiActivities;

  const contactDisplayName = getFullName(contact ?? {}) || contact?.firstName;

  const { topActivity, chronologicalActivities } = useMemo(
    () => resolveAndSortActivities(voiceAiActivities, t),
    [voiceAiActivities, t]
  );

  const hasVoiceAiActivities = voiceAiInUnifiedInboxEnabled && chronologicalActivities.length > 0;
  const callBubbleSubtitle = hasVoiceAiActivities && !isRobocall ? t('AI Receptionist') : bubbleInfo.subtitle;

  // Get the hover timestamp label for robocalls (includes voicemail distinction)
  const robocallSenderLabel = useMemo(() => {
    if (!isRobocall) return undefined;
    const isVoicemail = callItem.callResult === CallResult.CALL_RESULT_VOICEMAIL;
    return isVoicemail ? t('Auto Message Voicemail') : t('Auto Message');
  }, [isRobocall, callItem.callResult, t]);

  // TODO: hiding delete state until we support showing who deleted the item in the UI
  // callItem.deletedBy && callItem.deletedBy && deleterName;
  const isCallItemDeleted = false;

  const robocallErrorMessage = useMemo(() => {
    if (!isRobocall) return undefined;
    switch (callItem.callResult) {
      case CallResult.CALL_RESULT_MISSED:
        return t('Not Delivered');
      case CallResult.CALL_RESULT_ERROR:
      case CallResult.CALL_RESULT_INVALID_DESTINATION:
        return t('Delivery Error');
      default:
        return undefined;
    }
  }, [isRobocall, callItem.callResult, t]);

  const callPrimaryActions = [
    {
      label: t('Tag Call'),
      onClick: openTagSelection,
      icon: 'label',
      trackingId: `${InboxPrefixes.Tag}-tag-call`,
    },
  ];

  const handleOpenCallInsight = useCallback(
    ({ autoplayRecording = false }: { autoplayRecording?: boolean } = {}) => {
      emitOpenCallInsightPanel({
        channelId: callItem.channelId,
        includeCallIntelligence: true,
        locationId: callItem.locationId,
        autoplayRecording,
      });
    },
    [callItem.channelId, callItem.locationId]
  );

  // Robocalls don't have call insight or voicemail panels
  const handleCallAction = useCallback(() => {
    if (isRobocall) {
      return; // No action for robocalls
    }
    if (bubbleInfo.resultKey === 'voicemail') {
      emitOpenVoicemailInsightPanel({
        voicemailId: callItem.channelId,
        locationId: callItem.locationId,
      });
    } else {
      handleOpenCallInsight();
    }
  }, [
    emitOpenVoicemailInsightPanel,
    bubbleInfo.resultKey,
    callItem.channelId,
    callItem.locationId,
    handleOpenCallInsight,
    isRobocall,
  ]);

  const handlePlayAction = useCallback(() => {
    if (isRobocall) {
      handleCallAction();
      return;
    }

    if (bubbleInfo.resultKey === 'voicemail') {
      emitOpenVoicemailInsightPanel({
        voicemailId: callItem.channelId,
        locationId: callItem.locationId,
        autoplayRecording: true,
      });
      return;
    }

    handleOpenCallInsight({ autoplayRecording: true });
  }, [
    bubbleInfo.resultKey,
    callItem.channelId,
    callItem.locationId,
    emitOpenVoicemailInsightPanel,
    handleCallAction,
    handleOpenCallInsight,
    isRobocall,
  ]);

  return (
    <motion.div
      role={'button'}
      tabIndex={-1}
      css={[
        {
          display: 'flex',
          alignItems: 'center',
          transition: 'background-color 300ms ease-in-out, padding 300ms ease-in-out, margin 300ms ease-in-out',
          padding: theme.spacing(0, 2),
        },
      ]}
    >
      <div css={{ width: '100%' }}>
        <Chat
          direction={isRobocall ? 'outbound' : isInbound ? 'inbound' : 'outbound'}
          avatar={
            isRobocall ? (
              // Robocall avatar: Auto Message with reminder icon
              <Avatar {...avatarProps} name={t('Auto Message')} isWeave />
            ) : isInbound ? (
              <Photos.ContactProfilePhoto
                {...avatarProps}
                personId={callItem.personId ?? ''}
                locationId={callItem.locationId}
                firstName={contact?.firstName}
                lastName={contact?.lastName}
                name={getFullName(contact ?? {})}
              />
            ) : (
              <Avatar
                {...avatarProps}
                isUser
                firstName={sendingUser?.firstName}
                lastName={sendingUser?.lastName}
                name={getFullName(sendingUser ?? {})}
              />
            )
          }
          timestamp={formatTimestamp(callItem.startedAt)}
          error={robocallErrorMessage}
          senderName={isRobocall ? robocallSenderLabel : senderName}
          primaryActions={showTags ? callPrimaryActions : []}
        >
          {isCallItemDeleted ? (
            <Chat.Bubble
              variant={'deleted'}
              text={t('This call record was deleted on {{date}} by {{name}}', {
                date: new Date(callItem.deletedAt ?? '').toLocaleDateString(),
                name: deleterName || t('Unknown User'),
              })}
            />
          ) : (
            <Chat.Bubble
              ref={callBubbleRef}
              variant='call'
              callProps={{
                callResult: isRobocall ? 'outbound' : bubbleInfo.resultKey,
                callAction: isRobocall ? undefined : handleCallAction,
                playAction: isRobocall ? undefined : handlePlayAction,
              }}
              text={bubbleInfo.title}
              subText={callBubbleSubtitle}
              key={callItem.channelId}
              maxWidth={202}
              styles={{
                backgroundColor: theme.colors.white,
                borderRadius: theme.spacing(1),
                overflow: 'hidden',
                outline: `0.50px solid ${theme.colors.neutral20}`,
                '&:hover': {
                  backgroundColor: theme.colors.neutral5,
                },
              }}
            >
              {!isRobocall && hasVoiceAiActivities && topActivity && (
                <VoiceAiActivityAccordion
                  topActivity={topActivity}
                  activities={chronologicalActivities}
                  contactName={contactDisplayName}
                  attachedToCallBubble
                  scrollTargetRef={callBubbleRef}
                />
              )}
            </Chat.Bubble>
          )}

          {!isCallItemDeleted &&
            showTags &&
            (callItem.tags || []).map((tag) => (
              <ThreadItemTag
                key={tag.tagId}
                tagId={tag.tagId}
                tag={getV1TagFromTagId(tag.tagId)}
                removeTag={() => {
                  const originalTag = getV1TagFromTagId(tag.tagId);
                  if (originalTag) {
                    dismissTag(originalTag, tag.appliedBy);
                  }
                }}
                showFeedbackPopover={false}
                isAutoApplied={false}
                hasTagsFeedbackEndAdornment={false}
                groupId={callItem.locationId}
                onSmartTagFeedbackClick={() => {}}
              />
            ))}

          {/* Tag Selection Input */}
          {!isCallItemDeleted && showTags && tagSelectionIsOpen && (
            <Chat.Tag
              color='gray'
              label={
                <span ref={getTagSelectionTriggerProps().ref}>
                  <input
                    ref={tagSelectionInputRef}
                    type='text'
                    value={tagSearchValue}
                    autoFocus
                    onChange={(e) => setTagSearchValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === KeyNames.Escape) {
                        closeTagSelection();
                      }
                    }}
                    css={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      outline: 'none',
                      appearance: 'none',
                      width: `calc(${theme.spacing(1)} + ${tagInputWidth}px)`,
                      ':active': {
                        outline: 'none',
                        border: 'none',
                      },
                    }}
                  />
                </span>
              }
            />
          )}
        </Chat>

        {
          /* Invisible element to calculate the dynamic width of the tag input */
          tagSelectionIsOpen && (
            <span
              ref={tagInputWidthCalcRef}
              css={{
                position: 'absolute',
                opacity: 0,
                fontSize: theme.fontSize(14),
              }}
              tabIndex={-1}
            >
              {tagSearchValue}
            </span>
          )
        }

        {/* Tag Selection Popover */}
        <TagSelectionComponents.TagSelectionPopover {...tagPopoverProps} returnFocus={false} initialFocus={-1} />
      </div>
    </motion.div>
  );
};
